import { Component, OnInit, OnDestroy } from "@angular/core";
import { AuthService } from "src/app/shared/auth/auth.service";
import { Router, ActivatedRoute } from "@angular/router";
import { from, Subject, throwError } from "rxjs";
import { RegionStorageService } from "src/app/shared/region/region-storage.service";
import { tap, mergeMap, catchError, takeUntil, map } from "rxjs/operators";
import { CloudUserService } from "src/app/shared/user/cloud-user.service";
import { TimeService } from "src/app/shared/time/time.service";
import { ConfigurationService } from "src/app/shared/configuration/configuration.service";
import { RefreshTokenConfigurationService } from "src/app/shared/configuration/auth/refresh-token/refresh-token-configuration.service";
import { PKCEConfigurationService } from "src/app/shared/configuration/auth/p-k-c-e/p-k-c-e-configuration.service";
import { UserInfoConfigurationService } from "../shared/configuration/user-info/user-info-configuration.service";
import { UserInfoService } from "../shared/user-info/user-info.service";
import { HttpErrorResponse } from "@angular/common/http";
import { CloudIsGovCloudStorageService } from "../shared/cloud/cloud-is-gov-cloud-storage.service";

@Component({
  standalone: false,
  selector: "app-login-success",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  error = false;

  get configuration() {
    return this.configurationService.configuration;
  }

  get refreshTokenConfiguration() {
    return this.refreshTokenConfigurationService.configuration;
  }

  get queryParamMap() {
    return this.route.snapshot.queryParamMap;
  }

  get code() {
    return this.queryParamMap.get("code");
  }

  get region() {
    return this.queryParamMap.get("region");
  }

  get routeError() {
    return this.queryParamMap.get("error");
  }

  get pkceConfiguration() {
    return this.pkceConfigurationService.configuration;
  }

  get pkceEnabled() {
    return this.pkceConfiguration.enabled;
  }

  get storeRefreshTokens() {
    return this.refreshTokenConfiguration.storeRefreshTokens;
  }

  get userInfoEnabled() {
    return this.userInfoConfiguration.enabled;
  }

  get userInfoConfiguration() {
    return this.userInfoConfigurationService.configuration;
  }

  status;

  constructor(
    private authService: AuthService,
    private regionStorageService: RegionStorageService,
    private route: ActivatedRoute,
    private router: Router,
    private cloudUserService: CloudUserService,
    private timeService: TimeService,
    private configurationService: ConfigurationService,
    private refreshTokenConfigurationService: RefreshTokenConfigurationService,
    private pkceConfigurationService: PKCEConfigurationService,
    private userInfoLoaderService: UserInfoService,
    private userInfoConfigurationService: UserInfoConfigurationService,
    private cloudIsGovCloudService: CloudIsGovCloudStorageService
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
  }

  ngOnInit() {
    this.login();
  }

  login() {
    if (this.region) {
      this.storeRegion();
    }

    this.clearIsGovCloud();

    if (this.code) {
      this.codeState().pipe(takeUntil(this.destroy$)).subscribe();
    } else if (this.routeError) {
      this.errorState().pipe(takeUntil(this.destroy$)).subscribe();
    } else if (this.loadToken()) {
      this.tokenState().pipe(takeUntil(this.destroy$)).subscribe();
    } else {
      this.startState().pipe(takeUntil(this.destroy$)).subscribe();
    }
  }

  onRetry() {
    this.login();
  }

  errorState() {
    if (
      this.routeError === "interaction_required" ||
      this.routeError === "login_required" ||
      this.routeError === "consent_required"
    ) {
      this.clearAuthData();
      return this.startState();
    } else {
      throw this.routeError;
    }
  }

  navigateToFeatures() {
    return this.navigateByUrl("/features");
  }

  navigateByUrl(url) {
    return this.router.navigateByUrl(url);
  }

  storeRegion() {
    this.regionStorageService.storeRegion(this.region);
  }

  startState() {
    this.status = "Taking you to login...";
    if (this.pkceEnabled) {
      return from(this.pkceAuthorization());
    } else {
      return from(this.authorization());
    }
  }

  authorization() {
    return new Promise<void>(() => {
      window.location.href = this.createAuthorizationRequestUrl();
    });
  }

  pkceAuthorization() {
    const verifier = this.generateVerifier();
    this.storeVerifier(verifier);
    return this.createCodeChallenge(verifier).then((codeChallenge) => {
      this.storeCodeChallenge(codeChallenge);
      window.location.href = this.createPkceAuthorizationRequestUrl(
        codeChallenge
      );
    });
  }

  storeCodeChallenge(codeChallenge) {
    this.authService.storeCodeChallenge(codeChallenge);
  }

  clearIsGovCloud() {
    this.cloudIsGovCloudService.clearCloudIsGovCloud();
  }
  
  codeState() {
    if (this.pkceEnabled) {
      const verifier = this.loadVerifier();
      if (verifier) {
        this.status = "...";
        return this.runPkceAuthorizationCodeExchangeRequest(
          this.code,
          verifier
        ).pipe(
          tap((data: any) => {
            if (data.access_token) {
              this.storeToken(data.access_token);
              this.storeTokenStoreTime(this.now());
            }
            if (data.expires_in) {
              this.storeExpiresIn(data.expires_in);
            }
            if (this.storeRefreshTokens && data.refresh_token) {
              this.storeRefreshToken(data.refresh_token);
            }
          }),
          mergeMap(() => {
            return this.tokenState();
          }),
          catchError((e) => {
            this.error = true;
            if (this.isOutOfLicense(e)) {
              this.status = "No valid entitlement";
            } else if (e.status === 401 || e.status === 403) {
              this.status = 'Unauthorised';
            } else {
              this.status = null;
            }
            return throwError(e);
          })
        );
      } else {
        this.error = true;
        throw new Error("Verifier not found");
      }
    } else {
      this.status = "...";
      return this.runAuthorizationCodeExchange().pipe(
        tap((data: any) => {
          if (data.access_token) {
            this.storeToken(data.access_token);
            this.storeTokenStoreTime(this.now());
          }
          if (data.expires_in) {
            this.storeExpiresIn(data.expires_in);
          }
          if (this.storeRefreshTokens && data.refresh_token) {
            this.storeRefreshToken(data.refresh_token);
          }
        }),
        mergeMap(() => {
          return this.tokenState();
        }),
        catchError((e) => {
          this.error = true;

          if (this.isOutOfLicense(e)) {
            this.status = "No valid entitlement";
          } else if (e.status === 401 || e.status === 403) {
            this.status = 'Unauthorised';
          } else {
            this.status = null;
          }
          return throwError(e);
        })
      );
    }
  }

  storeExpiresIn(expires_in) {
    return this.authService.storeExpiresIn(expires_in);
  }

  storeTokenStoreTime(time) {
    return this.authService.storeTokenStoreTime(time);
  }

  now() {
    return this.timeService.now();
  }

  tokenState() {
    if (this.userInfoEnabled) {
      return this.getUserInfo().pipe(
        tap(() => {
          this.navigateToFeatures();
        }),
        catchError((e) => {
          this.error = true;
          this.status = null;
          return throwError(e);
        })
      );
    } else {
      return this.getCloudUser().pipe(
        tap(() => {
          this.navigateToFeatures();
        }),
        catchError((e) => {
          this.error = true;
          this.status = null;
          return throwError(e);
        })
      );
    }
  }

  getCloudUser() {
    return this.cloudUserService.getCloudUser();
  }

  clearAuthData() {
    return this.authService.clearData();
  }

  generateVerifier() {
    return this.authService.generateVerifier();
  }

  storeVerifier(verifier) {
    return this.authService.storeVerifier(verifier);
  }

  createAuthorizationRequestUrl() {
    return this.authService.createAuthorizationRequestUrl();
  }

  createPkceAuthorizationRequestUrl(codeChallenge) {
    return this.authService.createPkceAuthorizationRequestUrl(codeChallenge);
  }

  async createCodeChallenge(verifier) {
    return await this.authService.createCodeChallenge(verifier);
  }

  loadVerifier() {
    return this.authService.loadVerifier();
  }

  runPkceAuthorizationCodeExchangeRequest(code, verifier) {
    return this.authService.runPkceAuthorizationCodeExchange(code, verifier);
  }

  runAuthorizationCodeExchange() {
    return this.authService.runAuthorizationCodeExchange(this.code);
  }

  storeToken(token) {
    return this.authService.storeToken(token);
  }

  storeRefreshToken(refreshToken) {
    this.authService.storeRefreshToken(refreshToken);
  }

  loadToken() {
    return this.authService.loadToken();
  }

  getUserInfo() {
    return this.userInfoLoaderService.getUserInfo();
  }

  clearTokenCache() {
    return this.authService.clearToken();
  }

  isOutOfLicense(e) {
    return (
      e instanceof HttpErrorResponse &&
      e.status === 401 &&
      e.error &&
      (e.error.indexOf("Unauthorised. Licence could not be obtained.") > -1 ||
        e.error.indexOf("Invalid Entitlement. Status: Denied") > -1 ||
        e.error.indexOf("Invalid Entitlement. Status: NoLicense") > -1)
    );
  }
}
